import { useEffect, useState } from "react";
import { safeStorage } from "../helpers/safeStorage";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useAuth } from "../helpers/useAuth";
import { usePromoCampaign } from "../helpers/usePromoCampaign";
import { bandeauPromoMasque, cleFermeturePromo, ctaSourcePromo } from "../helpers/promoBannerRules";
import styles from "./PromoCampaignBanner.module.css";

/**
 * Bandeau de la campagne promo active affiché en haut de toutes les pages
 * pour les visiteurs anonymes, uniquement pendant la fenêtre de campagne.
 *
 * - Caché pour les utilisateurs authentifiés (ils ont leur propre parcours).
 * - Caché sur les pages où il ferait doublon ou gênerait la conversion
 *   (tarifs = bannière dédiée, checkout, inscription, login, espace admin).
 * - Dismissible : la fermeture est mémorisée en localStorage pour la campagne.
 * - Monté globalement dans App.tsx (sous AuthProvider, dans le Router).
 *
 * Pages serveur (Twig et prérendues) : même bandeau posé par nav.js (nav/promoBanner.ts),
 * règles communes dans helpers/promoBannerRules.ts.
 */

export function PromoCampaignBanner() {
  const { authState } = useAuth();
  const { pathname } = useLocation();
  const { campaign } = usePromoCampaign();
  const [dismissed, setDismissed] = useState(true);

  const dismissKey = campaign ? cleFermeturePromo(campaign.code) : "";

  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(safeStorage.getItem(dismissKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      if (dismissKey) safeStorage.setItem(dismissKey, "1");
    } catch {
      /* localStorage indisponible : on ferme quand même pour la session */
    }
  };

  if (!campaign) return null;
  if (dismissed) return null;
  if (authState.type !== "unauthenticated") return null;
  if (bandeauPromoMasque(pathname)) return null;

  return (
    <div className={styles.banner} role="note">
      <div className={styles.inner}>
        <span className={styles.tag}>{campaign.name}</span>
        <span className={styles.text}>
          <strong>{campaign.amountOff}€ offerts</strong> sur l'abonnement annuel
          {" "}· jusqu'au {campaign.endLabel}.
        </span>
        <Link
          to={`/tarifs/?cta_source=${ctaSourcePromo(campaign.code)}`}
          className={styles.cta}
          onClick={handleDismiss}
        >
          J'en profite
          <ArrowRight size={15} />
        </Link>
      </div>
      <button
        type="button"
        className={styles.close}
        onClick={handleDismiss}
        aria-label="Fermer la bannière"
      >
        <X size={16} />
      </button>
    </div>
  );
}
